import {ChangeDetectionStrategy, Component, computed, effect, inject} from '@angular/core';
import {CommonModule, NgClass} from '@angular/common';
import {ComposerPanelStateService} from '@app/model-composer/services/composer-panel-state.service';
import {ModelConfigService, ModelElement} from '@app/model-composer';
import {ComposerPanelTab} from '@app/model-composer/enums/composer-panel-tab.enum';
import {TranslatePipe} from "@ngx-translate/core";

@Component({
  selector: 'app-composer-panel-tabs',
  standalone: true,
  imports: [CommonModule, NgClass, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let active = activeTab();
    <div class="ipe:flex ipe:bg-[#F9F8F6]" role="tablist">
      @for (tab of tabs(); track tab.value) {
        @let isActive = tab.id === active;
        <button
          role="tab"
          class="
            ipe:flex-1
            ipe:px-4 ipe:py-5
            ipe:text-sm ipe:font-medium
            ipe:cursor-pointer ipe:select-none
            ipe:transition-colors ipe:duration-200
            ipe:border-b
            ipe:border-gray-100
            ipe:relative
            ipe:text-sm
            ipe:lg:text-sm
            ipe:tracking-[0.15em]

            ipe:font-medium
            ipe:transition-all
            font-walter
          "
          [attr.aria-selected]="isActive"
          [ngClass]="isActive
            ? 'ipe:text-[#18181b]'
            : 'ipe:text-gray-400 ipe:hover:text-gray-800'
          "
          (click)="set(tab.id)"
        >
          {{ tab.label | translate }}
          @if (isActive) {
            <span class="ipe:absolute ipe:bottom-0 ipe:left-0 ipe:w-full ipe:h-px ipe:bg-black"></span>
          }
        </button>
      }
    </div>
  `,
  styles: [
    `
      @import 'tailwindcss' prefix(ipe);
      @theme {
        --font-display: 'ABC Walter Neue', sans-serif;
        --font-roboto: 'ABC Walter Neue', sans-serif;
        --font-sans: 'ABC Walter Neue', sans-serif;
      }
    `
  ],
})
export class ComposerPanelTabsComponent {
  readonly panelState = inject(ComposerPanelStateService);
  readonly activeTab = this.panelState.activeTab;
  private readonly config = inject(ModelConfigService);
  readonly tabs = computed<any[]>(() => {
    const base: any[] = [
      { id: ComposerPanelTab.Architecture, label: 'composition.components' },
      { id: ComposerPanelTab.Style, label: 'composition.upholstery_and_finishes' },
    ];
    // return base;
    return this.hasCustomizationTab()
      ? [...base, { id: ComposerPanelTab.Customization, label: 'composition.add_ons_and_other' }]
      : base;
  });

  private readonly hasCustomizationTab = computed(() => {
    const elements: ModelElement[] = this.config.config()?.elements ?? [];
    return elements.some(el => this.hasCustomization(el));
  });

  constructor() {
    effect(() => {
      if (!this.hasCustomizationTab() && this.panelState.activeTab() === ComposerPanelTab.Customization) {
        this.panelState.setTab(ComposerPanelTab.Style);
      }
    });
  }

  set(tab: ComposerPanelTab): void {
    this.panelState.setTab(tab);
  }

  private hasCustomization(el: ModelElement): boolean {
    const c: any = (el as any).customization;
    if (!c) { return false; }
    if (typeof c === 'boolean') { return c; }
    if (Array.isArray(c)) { return c.length > 0; }
    if (typeof c === 'object') { return Object.keys(c).length > 0; }
    return true;
  }
}
